import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Clock, Briefcase, DollarSign, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Job } from '../../../types/jobs';
import { formatSalaryRange, formatExperience } from '../../../lib/careers';

interface JobDetailHeroProps {
  job: Job;
}

const JobDetailHero = ({ job }: JobDetailHeroProps) => {
  return (
    <section className="pt-32 pb-16 bg-black">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Link
            to="/careers"
            className="inline-flex items-center gap-2 text-accent hover:text-accent/80 transition-colors duration-300 mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Careers</span>
          </Link>
          
          <div className="flex items-center gap-3 mb-6">
            <span className="px-4 py-2 bg-accent/10 text-accent rounded-full text-sm font-medium">
              {job.category}
            </span>
            <span className="text-gray-400 text-sm">
              Posted {new Date(job.created_at).toLocaleDateString()}
            </span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-display font-semibold text-white mb-6 leading-[1.2]">
            {job.role}
          </h1>

          <div className="flex flex-wrap gap-6 text-gray-300 mb-8">
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-accent" />
              <span>{job.location}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-accent" />
              <span>{job.type} • {job.workplace}</span>
            </div>
            <div className="flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-accent" />
              <span>{formatExperience(job.experience)}</span>
            </div>
            {formatSalaryRange(job.salary_min, job.salary_max) && (
              <div className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-accent" />
                <span>{formatSalaryRange(job.salary_min, job.salary_max)}</span>
              </div>
            )}
          </div>

          <p className="text-xl text-gray-300 leading-relaxed">
            {job.about_role}
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default JobDetailHero;